import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { HiPlay, HiClock, HiCheckCircle, HiArrowTrendingUp } from 'react-icons/hi2'
import { fetchGames, fetchLiveGames } from '../services/api'
import PredictionOutcomeBadge from '../components/PredictionOutcomeBadge'

interface Game {
  game_id: string
  status: 'Upcoming' | 'Live' | 'Finished'
  home_team_hashtag: string
  away_team_hashtag: string
  home_team?: string | null
  away_team?: string | null
  start_time: string
  home_score?: number | null
  away_score?: number | null
  predicted_winner?: string | null
}

const HomePage: React.FC = () => {
  const [games, setGames] = useState<Game[]>([])
  const [liveGames, setLiveGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState<'Finished' | 'Live' | 'Upcoming'>('Finished')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadGames()
  }, [])

  const loadGames = async () => {
    try {
      setLoading(true)
      const [allGames, live] = await Promise.all([
        fetchGames(),
        fetchLiveGames()
      ])
      
      // Debug: Log first game to check fields
      if (allGames && allGames.length > 0) {
        console.log('Sample game data:', allGames[0])
      }
      
      // Sort games with better ordering logic
      const sortedGames = sortGames(allGames)
      setGames(sortedGames)
      setLiveGames(live)
    } catch (err) {
      setError('Failed to load games')
      console.error('Error loading games:', err)
    } finally {
      setLoading(false)
    }
  }

  const sortGames = (games: Game[]) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    return games.sort((a, b) => {
      const aDate = new Date(a.start_time)
      const bDate = new Date(b.start_time)
      
      // First, separate by status priority
      const statusPriority = {
        'Live': 0,
        'Upcoming': 1,
        'Finished': 2
      }
      
      const aStatusPriority = statusPriority[a.status] || 3
      const bStatusPriority = statusPriority[b.status] || 3
      
      if (aStatusPriority !== bStatusPriority) {
        return aStatusPriority - bStatusPriority
      }
      
      // For same status, sort by date
      if (a.status === 'Upcoming') {
        // Upcoming games: show today's games first, then future games chronologically
        const aIsToday = aDate >= today && aDate < new Date(today.getTime() + 24 * 60 * 60 * 1000)
        const bIsToday = bDate >= today && bDate < new Date(today.getTime() + 24 * 60 * 60 * 1000)
        
        if (aIsToday && !bIsToday) return -1
        if (!aIsToday && bIsToday) return 1
        if (aIsToday && bIsToday) return aDate.getTime() - bDate.getTime()
        
        // Future games: chronological order
        return aDate.getTime() - bDate.getTime()
      } else if (a.status === 'Finished') {
        // Finished games: most recent first (reverse chronological)
        return bDate.getTime() - aDate.getTime()
      } else {
        // Live games: by start time
        return aDate.getTime() - bDate.getTime()
      }
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Live':
        return <HiPlay className="h-4 w-4 text-orange-500 animate-pulse" />
      case 'Upcoming':
        return <HiClock className="h-4 w-4 text-orange-500" />
      case 'Finished':
        return <HiCheckCircle className="h-4 w-4 text-orange-500" />
      default:
        return null
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Live':
        return 'bg-orange-100 text-orange-800'
      case 'Upcoming':
        return 'bg-orange-100 text-orange-800'
      case 'Finished':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const renderGamesByStatus = () => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    // Group games by status
    const upcomingGames = games.filter(game => game.status === 'Upcoming')
    const finishedGames = games.filter(game => game.status === 'Finished')
    
    // Further group upcoming games by today vs future
    const todayGames = upcomingGames.filter(game => {
      const gameDate = new Date(game.start_time)
      return gameDate >= today && gameDate < new Date(today.getTime() + 24 * 60 * 60 * 1000)
    })
    const futureGames = upcomingGames.filter(game => {
      const gameDate = new Date(game.start_time)
      return gameDate >= new Date(today.getTime() + 24 * 60 * 60 * 1000)
    })
    
    return (
      <>
        {/* Today's Games */}
        {activeFilter === 'Upcoming' && todayGames.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <HiClock className="h-5 w-5 text-orange-400" />
              <h3 className="text-xl sm:text-2xl font-bold text-white">Today's Games</h3>
              <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs font-medium rounded-full">
                {todayGames.length} game{todayGames.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {todayGames.map(renderGameCard)}
            </div>
          </div>
        )}
        
        {/* Future Games */}
        {activeFilter === 'Upcoming' && futureGames.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <HiClock className="h-5 w-5 text-orange-400" />
              <h3 className="text-xl sm:text-2xl font-bold text-white">Upcoming Games</h3>
              <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs font-medium rounded-full">
                {futureGames.length} game{futureGames.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {futureGames.map(renderGameCard)}
            </div>
          </div>
        )}
        
        {/* Finished Games */}
        {activeFilter === 'Finished' && finishedGames.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <HiCheckCircle className="h-5 w-5 text-orange-400" />
              <h3 className="text-xl sm:text-2xl font-bold text-white">Recent Games</h3>
              <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs font-medium rounded-full">
                {finishedGames.length} game{finishedGames.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {finishedGames.map(renderGameCard)}
            </div>
          </div>
        )}
      </>
    )
  }

  const getRelativeTime = (startTime: string) => {
    const now = new Date()
    const gameTime = new Date(startTime)
    const diffMs = gameTime.getTime() - now.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffMs < 0) {
      // Past game
      const absDiffHours = Math.abs(diffHours)
      const absDiffDays = Math.abs(diffDays)
      
      if (absDiffDays > 0) {
        return `${absDiffDays} day${absDiffDays !== 1 ? 's' : ''} ago`
      } else if (absDiffHours > 0) {
        return `${absDiffHours} hour${absDiffHours !== 1 ? 's' : ''} ago`
      } else {
        return 'Just finished'
      }
    } else {
      // Future game
      if (diffDays > 0) {
        return `In ${diffDays} day${diffDays !== 1 ? 's' : ''}`
      } else if (diffHours > 0) {
        return `In ${diffHours} hour${diffHours !== 1 ? 's' : ''}`
      } else {
        const diffMinutes = Math.floor(diffMs / (1000 * 60))
        return diffMinutes > 0 ? `In ${diffMinutes} min` : 'Starting now'
      }
    }
  }


  const renderGameCard = (game: Game) => {
    // Determine route based on game status
    const getGameRoute = () => {
      if (game.status === 'Finished') {
        return `/finished/${game.game_id}`
      } else if (game.status === 'Upcoming') {
        return `/upcoming/${game.game_id}`
      } else {
        return `/live/${game.game_id}`
      }
    }

    // Determine link text based on game status
    const getLinkText = () => {
      if (game.status === 'Finished') {
        return 'View Analysis →'
      } else if (game.status === 'Upcoming') {
        return 'View Preview →'
      } else {
        return 'View Live →'
      }
    }

    return (
      <Link
        key={game.game_id}
        to={getGameRoute()}
        className="game-card group flex flex-col min-h-[280px] sm:h-[320px]"
      >
        {/* Status Badge - Fixed at top */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 flex-shrink-0">
          <div className="flex items-center space-x-2">
            {getStatusIcon(game.status)}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(game.status)}`}>
              {game.status}
            </span>
          </div>
        </div>
        
        {/* Main Content - Flexible middle section */}
        <div className="text-center flex-1 flex flex-col justify-center">
          <div className="text-base sm:text-lg font-semibold text-white mb-2 min-h-[3rem] flex items-center justify-center">
            {(game.home_team || game.home_team_hashtag?.replace('#', '') || 'Home Team')} vs {(game.away_team || game.away_team_hashtag?.replace('#', '') || 'Away Team')}
          </div>
          
          {/* Score section - Always reserve space */}
          <div className="text-xl sm:text-2xl font-bold text-white mb-2 min-h-[2.5rem] flex items-center justify-center">
            {game.home_score !== null && game.away_score !== null ? (
              `${game.home_score} - ${game.away_score}`
            ) : (
              <span className="invisible">0 - 0</span>
            )}
          </div>

          <div className="text-xs sm:text-sm text-gray-400 mb-1 min-h-[1.25rem]">
            {new Date(game.start_time).toLocaleString()}
          </div>
          <div className="text-[11px] sm:text-xs text-gray-500 min-h-[1rem]">
            {getRelativeTime(game.start_time)}
          </div>
        </div>
        
        {/* Prediction Badge - Fixed position, always reserve space */}
        <div className="mt-4 flex justify-center min-h-[2.5rem] flex-shrink-0">
          {game.predicted_winner ? (
            <PredictionOutcomeBadge
              predictedWinner={game.predicted_winner}
              homeScore={game.home_score ?? null}
              awayScore={game.away_score ?? null}
              showOutcome={game.status === 'Finished'}
            />
          ) : (
            <div className="invisible">
              <div className="px-2 py-1"></div>
            </div>
          )}
        </div>
        
        {/* Link Text - Fixed at bottom */}
        <div className="mt-4 text-center flex-shrink-0 min-h-[1.5rem]">
          <span className="text-sm text-primary-500 group-hover:text-primary-400">
            {getLinkText()}
          </span>
        </div>
      </Link>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
          Football Sentiment Tracker (Beta v0.1)
        </h1>
        <p className="text-base sm:text-xl text-gray-400 mb-6 sm:mb-8">
          Match sentiment analysis for EPL matches
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">{error}</div>
          <button 
            onClick={loadGames}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      ) : (
        <>
          {/* Filter Bar */}
          <div className="w-full flex items-center justify-center">
            <div className="inline-flex w-full max-w-md bg-gray-900 shadow-sm rounded-lg border border-gray-800 overflow-hidden">
              <button
                onClick={() => setActiveFilter('Finished')}
                className={`flex-1 px-3 py-2 text-xs sm:text-sm font-medium ${activeFilter === 'Finished' ? 'bg-black text-orange-400 border-2 border-orange-500 rounded-l-lg' : 'text-gray-300 hover:bg-gray-800'}`}
              >
                Finished
              </button>
              <button
                onClick={() => setActiveFilter('Live')}
                className={`flex-1 px-3 py-2 text-xs sm:text-sm font-medium border-l border-r border-gray-800 ${activeFilter === 'Live' ? 'bg-black text-orange-400 border-2 border-orange-500' : 'text-gray-300 hover:bg-gray-800'}`}
              >
                Live
              </button>
              
              <button
                onClick={() => setActiveFilter('Upcoming')}
                className={`flex-1 px-3 py-2 text-xs sm:text-sm font-medium border-r border-gray-800 ${activeFilter === 'Upcoming' ? 'bg-black text-orange-400 border-2 border-orange-500 rounded-r-lg' : 'text-gray-300 hover:bg-gray-800'}`}
              >
                Upcoming
              </button>
            </div>
          </div>

          {/* Live Games Section (only when Live filter) */}
          {activeFilter === 'Live' && (
            <section>
              <div className="flex items-center space-x-2 mb-6">
                <HiArrowTrendingUp className="h-6 w-6 text-orange-400" />
                <h2 className="text-xl sm:text-2xl font-bold text-white">Live Games</h2>
                <h3 className="text-medium sm:text-medium font-bold text-white">(Scores are not updated in real-time)</h3>
                <span className="live-indicator">LIVE</span>
              </div>
              
              {liveGames.length === 0 ? (
                <div className="text-gray-400">No live games right now.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {liveGames.map(renderGameCard)}
                </div>
              )}
            </section>
          )}

          {/* Filtered Games Section for Upcoming / Finished */}
          {activeFilter !== 'Live' && (
            <section>
              {games.length === 0 ? (
                <>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">
                    {activeFilter === 'Upcoming' ? 'Upcoming Games' : 'Recent Games'}
                  </h2>
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">No games available</div>
                    <button 
                      onClick={loadGames}
                      className="btn-primary"
                    >
                      Refresh
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-8">
                  {renderGamesByStatus()}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  )
}

export default HomePage

